// frontend\src\components\InputUsageRuleBuilder.tsx
// Chip-style rule editor: "Use [2] for every product from [Wilwood] shipped".
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { InputUsageRule, InputRuleScope, StockItem, Supplier } from '../types';

interface ProductOption {
  product_id: number;
  variation_id: number;
  label: string;
}

// Cap on rendered matches — the list is only a picker, not a browser.
const MAX_VISIBLE_OPTIONS = 40;

interface ProductComboboxProps {
  options: ProductOption[];
  productId: number | null;
  variationId: number;
  onSelect: (productId: number, variationId: number) => void;
  disabled?: boolean;
}

/**
 * Type-to-filter product picker. A plain <select> is unusable once the catalog
 * grows, so this shows a text box that filters the list as characters are
 * entered — the same behaviour as the search on the stock table.
 */
function ProductCombobox({ options, productId, variationId, onSelect, disabled }: ProductComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find(
    (option) => option.product_id === productId && option.variation_id === variationId
  );

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    const pool = term ? options.filter((o) => o.label.toLowerCase().includes(term)) : options;
    return pool.slice(0, MAX_VISIBLE_OPTIONS);
  }, [options, query]);

  return (
    <div className="input-rule-combo" ref={containerRef}>
      <input
        type="text"
        className="input-rule-chip input-rule-chip-wide"
        placeholder="Search products..."
        disabled={disabled}
        value={open ? query : selected?.label ?? ''}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
      />
      {open && (
        <ul className="input-rule-combo-list">
          {matches.length === 0 && <li className="input-rule-combo-empty">No matching products</li>}
          {matches.map((option) => (
            <li
              key={`${option.product_id}:${option.variation_id}`}
              className={selected === option ? 'is-selected' : undefined}
              onClick={() => {
                onSelect(option.product_id, option.variation_id);
                setOpen(false);
                setQuery('');
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface InputUsageRuleBuilderProps {
  rules: InputUsageRule[];
  onChange: (rules: InputUsageRule[]) => void;
  suppliers: Supplier[];
  stockData: StockItem[];
  disabled?: boolean;
}

const SCOPE_LABELS: Record<InputRuleScope, string> = {
  all: 'product',
  supplier: 'product from',
  product: 'unit of',
};

export default function InputUsageRuleBuilder({
  rules,
  onChange,
  suppliers,
  stockData,
  disabled,
}: InputUsageRuleBuilderProps) {
  // A "sellable line" is what an order item points at: a simple product, or a
  // variation of a variable product. Variable parents are never sold directly.
  const productOptions = useMemo<ProductOption[]>(() => {
    const parentsById = new Map(stockData.map((item) => [item.id, item]));
    const variationParents = new Set(
      stockData
        .filter((item) => item.type === 'variation' && item.parent_product_id)
        .map((item) => item.parent_product_id as number)
    );

    const options: ProductOption[] = [];
    for (const item of stockData) {
      if (item.type === 'variation' && item.parent_product_id) {
        const parent = parentsById.get(item.parent_product_id);
        options.push({
          product_id: item.parent_product_id,
          variation_id: item.id,
          label: parent ? `${parent.name} (${item.name})` : item.name,
        });
      } else if (!variationParents.has(item.id)) {
        options.push({ product_id: item.id, variation_id: 0, label: item.name });
      }
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [stockData]);

  const updateRule = (index: number, patch: Partial<InputUsageRule>) => {
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  const removeRule = (index: number) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  const addRule = () => {
    onChange([...rules, { qty_per_unit: 1, scope: 'all', supplier_name: null, product_id: null, variation_id: 0 }]);
  };

  // Switching scope clears the previous scope's target so a stale supplier or
  // product can never be submitted with the wrong scope. The product is left
  // unset deliberately — picking one is a search, not a default.
  const changeScope = (index: number, scope: InputRuleScope) => {
    updateRule(index, {
      scope,
      supplier_name: scope === 'supplier' ? suppliers[0]?.name ?? null : null,
      product_id: null,
      variation_id: 0,
    });
  };

  return (
    <div className="input-rules">
      {rules.length === 0 && (
        <p className="input-rules-empty">
          No usage rules yet. Without one this input has no burn rate and will never be
          decremented.
        </p>
      )}

      {rules.map((rule, index) => (
        <div className="input-rule-row" key={rule.id ?? `new-${index}`}>
          <span className="input-rule-text">Use</span>

          <input
            className="input-rule-qty"
            type="number"
            min="0"
            step="0.001"
            value={rule.qty_per_unit}
            disabled={disabled}
            onChange={(e) => updateRule(index, { qty_per_unit: Number(e.target.value) })}
          />

          <span className="input-rule-text">for every</span>

          <select
            className="input-rule-chip"
            value={rule.scope}
            disabled={disabled}
            onChange={(e) => changeScope(index, e.target.value as InputRuleScope)}
          >
            {(Object.keys(SCOPE_LABELS) as InputRuleScope[]).map((scope) => (
              <option key={scope} value={scope}>
                {SCOPE_LABELS[scope]}
              </option>
            ))}
          </select>

          {rule.scope === 'supplier' && (
            <select
              className="input-rule-chip"
              value={rule.supplier_name ?? ''}
              disabled={disabled}
              onChange={(e) => updateRule(index, { supplier_name: e.target.value })}
            >
              <option value="">Select supplier…</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.name}>
                  {supplier.name}
                </option>
              ))}
            </select>
          )}

          {rule.scope === 'product' && (
            <ProductCombobox
              options={productOptions}
              productId={rule.product_id ?? null}
              variationId={rule.variation_id ?? 0}
              disabled={disabled}
              onSelect={(productId, variationId) =>
                updateRule(index, { product_id: productId, variation_id: variationId })
              }
            />
          )}

          <span className="input-rule-text">shipped</span>

          {!disabled && (
            <button
              type="button"
              className="input-rule-remove"
              title="Remove rule"
              onClick={() => removeRule(index)}
            >
              ✕
            </button>
          )}
        </div>
      ))}

      {!disabled && (
        <button type="button" className="action-button input-rule-add" onClick={addRule}>
          + Add rule
        </button>
      )}

      {rules.length > 1 && (
        <p className="input-rules-hint">
          When more than one rule matches the same shipped unit, the most specific one is
          used (product beats supplier beats all), so units are never counted twice.
        </p>
      )}
    </div>
  );
}
